import React, { useState } from "react";
import {
  PlusIcon,
  MinusIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  UserIcon,
  BuildingOffice2Icon,
  ClockIcon,
} from "@heroicons/react/24/solid";

import moment from "moment";
import { getTimeSince } from "@/utils/EtGetTimeSince";

const EtConversationContactNoteCard = ({
  note,
  index = 0,
  onEdit,
  onDelete,
  showOwner = false, // ✅ 18 Jan 2026 - NEW (for Collective → Everyone)
  mode ="full", // 24 Jan 2026
}) => {

  // First Created - 16 Jan 2026
  // Author  - Atul Pandey
  // Purpose - Pure presentation card for displaying a contact conversation note. Read-only. Expand / collapse details. No AI, no actions, no mutations
  // Updated - 18 Jan 2026 - Refinements for Collective 
  // Updated - 23 Jan 2026 - Refinements for Edit / Update 
  // Updated - 24 Jan 2026 - to display content based on mode
  // Updated - 01 Feb 2026 - added mode to hide private mode message when the note is shared or displayed.
  
  // 0 - Validate incoming data
  // console.log('note : ', note)

  // 1 - State Variables
  const [expanded, setExpanded] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  // 2 - Data extraction from incoming note
  const {
    conversationText = "",
    conversationDate,
    updatedAt,
    contactId = {},
  } = note;

  const {
    contactName = "Self",
    contactNickName = "",
    contactOrgRole,
    contactOrgTitle,
    contactOrgId,
    contactTypeId,
  } = contactId;

  const role = contactOrgRole?.orgRoleName || "";
  const title = contactOrgTitle?.orgTitleName || "";
  const orgName = contactOrgId?.contactOrganizationName || "";
  const contactType = contactTypeId?.contactTypeName || "";

  // 3 - Create Markup
  const createMarkup = (html) => ({ __html: html });

  // 4 - Show only when note is there
  if (!note) return null;


  return (
    <div className="bg-gray-100 rounded shadow-sm border m-1 p-1">

      {/* Header */}
      <div className="grid grid-cols-12 gap-2 items-center m-1 p-1">

        {/* Expand */}
        <div className="col-span-3 flex items-center">
          <button
            onClick={() => setExpanded(prev => !prev)}
            className="rounded-full hover:bg-white p-1"
          >
            {expanded ? (
              <MinusIcon className="h-4 w-4 text-orange-600" />
            ) : (
              <PlusIcon className="h-4 w-4 text-orange-600" />
            )}
          </button>

          <p className="ml-1 text-sm font-bold text-gray-600">
            {index + 1}.
          </p>

          {/* Owner (Collective → Everyone) */}
          {showOwner && note?.userId?._id && (
            <div className="ml-3 mr-2 flex items-center gap-2 bg-white rounded-full px-2 py-[2px] border">
                <div className="h-5 w-5 rounded-full bg-orange-100 flex items-center justify-center text-[10px] font-bold text-orange-700">
                {(note.userId.name || note.userId.username || "U")[0]}
                </div>
                <p className="text-[10px] font-semibold text-gray-700">
                {(note.userId.name || note.userId.username || "User").split(" ")[0]}
                </p>
            </div>
           )}

            
            {/* Contact */}
           <UserIcon className="h-5 w-5 text-orange-600 mx-2 ml-4" />
           
           <div>
            <p className="text-sm font-semibold text-gray-800">
              {contactName}
            </p>
            <p className="text-xs text-gray-500 italic">
              {title} {role && `• ${role}`}
            </p>
            </div>
        </div>

        {/* Type */}
        <div className="col-span-1 text-xs text-gray-500">
          {contactType}
        </div>

        {/* Org */}
        <div className="col-span-2 flex items-center text-xs text-gray-600">
          <BuildingOffice2Icon className="h-4 w-4 text-orange-600 mr-1" />
          {orgName}
        </div>

        {/* Summary Row */}
        {mode === "full" && (
          <div className="col-span-3 text-xs text-gray-700 truncate">
            <span className="font-bold text-gray-600">Conversation:</span>{" "}
            <span
              dangerouslySetInnerHTML={createMarkup(conversationText)}
              className="line-clamp-1"
            />
          </div>
        )}
        {mode !== "full" && (
          <div className="col-span-3 text-xs text-gray-700 truncate">
            <span className="font-bold text-gray-600">Conversation:</span>{" "}
            <p className="text-orange-600 text-xs">
                Shared privately 
              </p>      
          </div>
        )}

        <div className="col-span-2 flex justify-end items-center text-[10px] text-gray-500">
          <ClockIcon className="h-4 w-4 text-lime-600 mr-1" />
          {moment(conversationDate).format("DD MMM YYYY, hh:mm A")}
        </div>

        {/* Actions */}
        {mode === "full" && (
          <div className="col-span-1 flex justify-end space-x-2">
              <button
                  onClick={() => onEdit?.(note)}
                  className="p-1 rounded hover:bg-orange-100"
                  title="Edit conversation"
              >
                  <PencilSquareIcon className="h-4 w-4 text-orange-600" />
              </button>

              <button
                  onClick={() => onDelete?.(note)}
                  className="p-1 rounded hover:bg-red-100"
                  title="Delete conversation"
              >
                  <TrashIcon className="h-4 w-4 text-red-600" />
              </button>
          </div>
        )}

      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="mt-3 bg-white rounded border p-2">

             <div className="ml-2 my-2">
              <p className="font-bold text-sm">Conversation Details</p>      
            </div>

            {mode === "full" && (
            <div
              className="text-xs text-gray-700 border-t p-2 fix-nested-lists"
              dangerouslySetInnerHTML={createMarkup(conversationText)}
            />
            )}

            {mode !== "full" && (    // 01 Feb 2026 - added mode to hide this when the note is shared or displayed.
              <div
                className="text-xs text-gray-700 border-t p-2 fix-nested-lists"
                
              >
                <p className="text-orange-600 text-xs">
                      Conversation has been kept private by the user 
                </p>

              </div>
            )}
          
          <div className="flex justify-end mt-2 border-t pt-2 ">
            <div className="flex items-center">
                <p className="text-[9px] text-gray-600 mx-2">Last updated :</p>
                <p className="text-[9px] px-2 py-[2] rounded-full bg-lime-300 text-gray-800">{getTimeSince(updatedAt)}</p>
            </div>
             
          </div>
        </div>
      )}
    </div>
  );
};

export default EtConversationContactNoteCard;